import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, Avatar, Autocomplete, TextField, InputAdornment, CircularProgress, Chip,
} from '@mui/material';
import {
    Search as SearchIcon, SwapHoriz as SwapHorizIcon, LocationOn as LocationOnIcon,
    Event as EventIcon, Block as BlockIcon, WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNotify } from '../../contexts/FeedbackContext';

// delivery_date llega como 'YYYY-MM-DD': se arma en hora local para que no se corra un día.
const formatDate = (value) => {
    const [y, m, d] = String(value).split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return value;
    return new Date(y, m - 1, d).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
};

const formatMoney = (value) =>
    Number(value).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts.length >= 2 ? parts[0][0] + parts[1][0] : name.substring(0, 2)).toUpperCase();
};

const RouteChips = ({ client }) => {
    if (!client?.is_wholesale) return null;
    const routes = client.delivery_schedule?.routes || [];
    if (routes.length === 0) {
        return <Chip size="small" label="Sin recorrido" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.7rem', height: 20 }} />;
    }
    return routes.map((r, idx) => (
        <Chip
            key={idx}
            size="small"
            label={`${r.day_label} (${r.route_name})`}
            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem', height: 20 }}
        />
    ));
};

/**
 * Selector de cliente embebido en la pantalla del pedido.
 * Sin cliente (o al tocar "Cambiar") muestra un buscador con resultados del servidor;
 * con cliente elegido se colapsa a una línea.
 */
const ClientPicker = ({ client, orderType, onSelect, deliveryDate, itemsCount = 0 }) => {
    const clientType = orderType === 'business' ? 'wholesale' : 'retail';
    const [editing, setEditing] = useState(!client);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const requestSeq = useRef(0);
    const notify = useNotify();

    useEffect(() => {
        if (!client) setEditing(true);
    }, [client]);

    useEffect(() => {
        if (!editing) return undefined;
        const seq = ++requestSeq.current;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ is_active: 'true', client_type: clientType, ordering: 'name' });
                if (inputValue.trim()) params.set('search', inputValue.trim());
                const response = await axios.get(`/api/clients/?${params.toString()}`);
                if (seq === requestSeq.current) setOptions(response.data.results || response.data);
            } catch (error) {
                console.error('Error fetching clients:', error);
                if (seq === requestSeq.current) notify('No se pudieron buscar clientes. Revisá la conexión e intentá de nuevo.', 'error');
            } finally {
                if (seq === requestSeq.current) setLoading(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [inputValue, clientType, editing, notify]);

    const handleSelect = async (_, selected) => {
        if (!selected) return;
        setSaving(true);
        try {
            await onSelect(selected);
            setEditing(false);
            setInputValue('');
        } catch {
            // El error ya lo muestra la pantalla del pedido
        } finally {
            setSaving(false);
        }
    };

    if (client && !editing) {
        const debt = Number(client.current_balance) || 0;
        return (
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5 }}>
                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 40, height: 40, fontSize: '0.875rem', fontWeight: 600, flexShrink: 0 }}>
                    {getInitials(client.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 600, color: '#0e141b', fontSize: '0.9375rem', minWidth: 0 }} noWrap>
                            {client.name}
                        </Typography>
                        {client.cuit && (
                            <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>CUIT {client.cuit}</Typography>
                        )}
                        {client.is_blocked && (
                            <Chip size="small" icon={<BlockIcon />} label="Cuenta bloqueada" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.7rem', height: 20, '& .MuiChip-icon': { color: '#c62828', fontSize: 14 } }} />
                        )}
                        {!client.is_blocked && debt > 0 && (
                            <Chip size="small" label={`Saldo ${formatMoney(debt)}`} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <LocationOnIcon sx={{ color: '#4e7397', fontSize: 16 }} />
                            <Typography sx={{ color: '#4e7397', fontSize: '0.8125rem' }}>
                                {[client.delivery_address, client.delivery_city_name || client.city].filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                        {deliveryDate && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EventIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                                <Typography sx={{ color: '#0e141b', fontSize: '0.8125rem', fontWeight: 500 }}>
                                    Entrega {formatDate(deliveryDate)}
                                </Typography>
                            </Box>
                        )}
                        <RouteChips client={client} />
                    </Box>
                </Box>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => setEditing(true)}
                    aria-label="Cambiar cliente"
                    sx={{
                        flexShrink: 0, textTransform: 'none', borderColor: '#d0dbe7', color: '#0e141b',
                        minWidth: { xs: 0, sm: 64 }, px: { xs: 1, sm: 1.5 },
                        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                        '&:hover': { borderColor: '#1976d2', bgcolor: '#e3f2fd' },
                    }}
                >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Cambiar</Box>
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Typography sx={{ fontWeight: 600, color: '#1976d2', fontSize: '0.8125rem', mb: 1 }}>
                1 · Elegí el cliente {clientType === 'wholesale' ? 'mayorista' : 'minorista'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                    fullWidth
                    openOnFocus
                    autoHighlight
                    options={options}
                    loading={loading}
                    disabled={saving}
                    value={null}
                    inputValue={inputValue}
                    onInputChange={(_, value, reason) => { if (reason !== 'reset') setInputValue(value); }}
                    onChange={handleSelect}
                    filterOptions={(x) => x}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    noOptionsText={inputValue ? 'No se encontraron clientes' : 'Escribí para buscar'}
                    loadingText="Buscando..."
                    renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                            <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 32, height: 32, fontSize: '0.8rem', fontWeight: 600 }}>
                                {getInitials(option.name)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0e141b' }} noWrap>
                                    {option.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }} noWrap>
                                    {[option.cuit, option.delivery_address, option.delivery_city_name || option.city].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
                                <RouteChips client={option} />
                            </Box>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoFocus
                            size="small"
                            placeholder="Nombre, CUIT, código o teléfono"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#4e7397' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <>
                                        {(loading || saving) && <CircularProgress size={18} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                {client && (
                    <Button onClick={() => { setEditing(false); setInputValue(''); }} sx={{ textTransform: 'none', color: '#4e7397', whiteSpace: 'nowrap' }}>
                        Cancelar
                    </Button>
                )}
            </Box>
            {client && itemsCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                    <WarningAmberIcon sx={{ color: '#e65100', fontSize: 18 }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: '#e65100' }}>
                        El pedido ya tiene {itemsCount} {itemsCount === 1 ? 'producto' : 'productos'}: mantienen los precios de {client.name}.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ClientPicker;
